import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";


import { 
  LayoutDashboard, 
  ShoppingBag, 
  DollarSign, 
  Package, 
  ImageIcon, 
  Tags, 
  CreditCard, 
  MessageSquare,
  LogOut,
  Loader2,
  ShieldAlert
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useProducts } from "@/context/ProductContext";
import { getAdminStatus, claimFirstAdminRole } from "@/lib/store.functions";

// Components
import { AdminLogin } from "@/components/admin/AdminLogin";
import { AdminDashboard } from "@/components/admin/AdminDashboard";
import { AdminOrders } from "@/components/admin/AdminOrders";
import { AdminFinancial } from "@/components/admin/AdminFinancial";
import { AdminProducts } from "@/components/admin/AdminProducts";
import { AdminWhatsApp } from "@/components/admin/AdminWhatsApp";

// Placeholder components for those not yet fully extracted
function BannersManager() {
  const [banners, setBanners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBanners = async () => {
    setLoading(true);
    const { data } = await supabase.from('banners').select('*').order('created_at', { ascending: false });
    setBanners(data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const removeBanner = async (id: string) => {
    const { error } = await supabase.from('banners').delete().eq('id', id);
    if (!error) {
      toast.success("Banner removido!");
      fetchBanners();
    }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <ImageIcon className="h-6 w-6 text-pink-500" />
          Gerenciamento de Banners
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {banners.length === 0 ? (
          <p className="text-gray-500 text-center py-12 col-span-full">Nenhum banner cadastrado.</p>
        ) : (
          banners.map((banner: any) => (
            <div key={banner.id} className="relative aspect-[21/9] rounded-2xl overflow-hidden group border shadow-sm">
              <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button size="sm" variant="destructive" onClick={() => removeBanner(banner.id)}>Excluir</Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


function CategoriesManager() {
  const { categories, addCategory, removeCategory } = useProducts();
  const [newCat, setNewCat] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = async () => {
    if (!newCat.trim()) return;
    setIsAdding(true);
    await addCategory(newCat.trim());
    setNewCat("");
    setIsAdding(false);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold flex items-center gap-2">
        <Tags className="h-6 w-6 text-pink-500" />
        Categorias
      </h2>
      <div className="flex gap-2">
        <Input 
          className="flex-1 h-10 px-3 rounded-xl border" 
          placeholder="Nova categoria..." 
          value={newCat} 
          onChange={e => setNewCat(e.target.value)}
          disabled={isAdding}
        />
        <Button onClick={handleAdd} disabled={isAdding}>
          {isAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Adicionar"}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {categories.filter(c => c !== "Todos").map(cat => (
          <div key={cat} className="bg-white px-4 py-2 rounded-xl border flex items-center justify-between gap-4">
            <span className="font-medium">{cat}</span>
            <button 
              onClick={() => removeCategory(cat)} 
              className="text-red-500 hover:text-red-700 font-bold"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


export const Route = createFileRoute("/admin/")({
  component: AdminPage,
});

function AdminPage() {
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canClaim, setCanClaim] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [orders, setOrders] = useState<any[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  
  const { 
    products, 
    categories, 
    addProduct, 
    updateProduct, 
    removeProduct, 
    addCategory, 
    removeCategory,
    getSetting,
    saveSetting,
    isLoading: isLoadingProducts
  } = useProducts();

  const fetchOrders = async () => {
    setIsLoadingOrders(true);
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setOrders(data || []);
    } catch (err) {
      console.error("Erro ao carregar pedidos:", err);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const checkStatus = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setUser(session?.user ?? null);
    
    if (session?.user) {
      const status = await getAdminStatus();
      setIsAdmin(status.isAdmin);
      setCanClaim(status.canClaimFirstAdmin);
    } else {
      setIsAdmin(false);
      setCanClaim(false);
    }
    setIsChecking(false);
  };

  useEffect(() => {
    checkStatus();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkStatus();
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchOrders();
      const channel = supabase
        .channel('admin_orders')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => {
          fetchOrders();
        })
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [isAdmin]);

  const handleClaimAdmin = async () => {
    try {
      await claimFirstAdminRole();
      toast.success("Você agora é o administrador do sistema!");
      checkStatus();
    } catch (err: any) {
      toast.error(err.message || "Erro ao assumir controle.");
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Sessão encerrada.");
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-pink-500" />
      </div>
    );
  }

  if (!user) {
    return <AdminLogin onLogin={checkStatus} />;
  }

  if (!isAdmin) {
    if (canClaim) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <Card className="max-w-md w-full p-8 text-center space-y-6 rounded-[32px] shadow-xl">
            <div className="bg-yellow-50 p-4 rounded-full w-fit mx-auto">
              <ShieldAlert className="h-12 w-12 text-yellow-500" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold">Nenhum administrador encontrado</h1>
              <p className="text-gray-500">
                Você é o primeiro usuário a acessar o painel. Deseja assumir o controle administrativo do Ateliê?
              </p>
            </div>
            <Button onClick={handleClaimAdmin} className="w-full bg-pink-500 hover:bg-pink-600 h-12 rounded-xl font-bold">
              Sim, tornar-me Administrador
            </Button>
          </Card>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md w-full p-8 text-center space-y-6 rounded-[32px] shadow-xl">
          <div className="bg-red-50 p-4 rounded-full w-fit mx-auto">
            <ShieldAlert className="h-12 w-12 text-red-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Acesso Negado</h1>
            <p className="text-gray-500">
              Sua conta ({user.email}) não possui permissões administrativas.
            </p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="w-full h-12 rounded-xl">
            Sair e entrar com outra conta
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="h-6 w-6 text-pink-500" />
            <h1 className="text-xl font-serif font-bold hidden sm:block">Painel de Controle</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs bg-pink-100 text-pink-600 px-2 py-1 rounded-full font-medium hidden sm:block">
              {user.email}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-gray-500 hover:text-red-600 gap-2 rounded-full"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
              <span className="text-xs font-bold">Sair</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <Tabs defaultValue="dashboard" className="space-y-8">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-8 h-auto md:h-12 bg-white border p-1 overflow-x-auto scrollbar-hide">
            <TabsTrigger value="dashboard" className="flex items-center gap-2 data-[state=active]:text-pink-600 py-2">
              <LayoutDashboard className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2 data-[state=active]:text-pink-600 py-2">
              <ShoppingBag className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Pedidos</span>
            </TabsTrigger>
            <TabsTrigger value="financial" className="flex items-center gap-2 data-[state=active]:text-pink-600 py-2">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Financeiro</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2 data-[state=active]:text-pink-600 py-2">
              <Package className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Produtos</span>
            </TabsTrigger>
            <TabsTrigger value="banners" className="flex items-center gap-2 data-[state=active]:text-pink-600 py-2">
              <ImageIcon className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Banners</span>
            </TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-2 data-[state=active]:text-pink-600 py-2">
              <Tags className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Categorias</span>
            </TabsTrigger>
            <TabsTrigger value="integrations" className="flex items-center gap-2 data-[state=active]:text-pink-600 py-2">
              <CreditCard className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Pagamentos</span>
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="flex items-center gap-2 data-[state=active]:text-pink-600 py-2">
              <MessageSquare className="h-4 w-4" />
              <span className="text-xs sm:text-sm">WhatsApp</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <AdminDashboard orders={orders} />
          </TabsContent>

          <TabsContent value="orders">
            <AdminOrders 
              orders={orders} 
              isLoadingOrders={isLoadingOrders} 
              fetchOrders={fetchOrders} 
              products={products}
            />
          </TabsContent>

          <TabsContent value="financial">
            <AdminFinancial orders={orders} />
          </TabsContent>

          <TabsContent value="products">
            <AdminProducts 
              products={products} 
              categories={categories}
              addProduct={addProduct}
              updateProduct={updateProduct}
              removeProduct={removeProduct}
              addCategory={addCategory}
              removeCategory={removeCategory}
            />
          </TabsContent>

          <TabsContent value="banners">
            <BannersManager />
          </TabsContent>

          <TabsContent value="categories">
            <CategoriesManager />
          </TabsContent>

          <TabsContent value="integrations">
            <Card className="p-8 text-center text-gray-500">
              Integração de pagamentos em desenvolvimento.
            </Card>
          </TabsContent>

          <TabsContent value="whatsapp">
            <AdminWhatsApp 
              initialInstance={""} // Initial values handled by local state in the component
              initialWhatsapp={""}
              onSave={async (instance, whatsapp) => {
                await saveSetting('uazapi_instance', instance);
                await saveSetting('admin_whatsapp', whatsapp.replace(/\D/g, ''));
                toast.success("Configurações salvas!");
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
